import create from 'zustand';
import produce from 'immer';
import { getShareTradeTarget, putShareTradeTarget } from 'lib/api/shareTrade';
import {
  ShareTradeTargetParams,
  ShareTradeTargetResponse,
  UpdateTargetRequestBody,
} from 'typings/api/shareTradeTarget';
import alertMessage from 'global/AlertMessage';

interface State {
  data?: ShareTradeTargetResponse;
  isEmptyList: boolean;
  isLoading: boolean;
  isUpdateLoading: boolean;
  error?: Error;
  updateError?: Error;
}

interface Actions {
  loadShareTradeTarget(
    param?: Partial<ShareTradeTargetParams>,
    disableLoading?: boolean,
  ): Promise<ShareTradeTargetResponse>;
  loadUpdateTarget(requestBody: UpdateTargetRequestBody, withLoading?: boolean): Promise<void>;
  resetShareTradeTarget(): void;
}

const initialState: State = {
  data: undefined,
  isEmptyList: false,
  isLoading: false,
  isUpdateLoading: false,
  error: undefined,
  updateError: undefined,
};

const useShareTradeTarget = create<State & Actions>((set) => ({
  ...initialState,
  loadShareTradeTarget: async (params, disableLoading = false) => {
    const { initialLoad, ...restParams } = params;

    try {
      set({ isLoading: !disableLoading });
      const axiosResponse = await getShareTradeTarget({
        limit: 20,
        ...restParams,
      });
      const { data } = axiosResponse.data;

      set((currstate) =>
        produce(currstate, (state: State) => {
          if (params?.cursor) {
            state.data = {
              ...data,
              targets: [...state.data.targets, ...data.targets],
            };
          } else state.data = data;

          if (
            data.targets.length === 0 &&
            !params?.keyword &&
            initialLoad
          ) {
            state.isEmptyList = true;
          }

          state.isLoading = false;
          state.error = undefined;
        }),
      );

      return data;
    } catch (error) {
      set({ isLoading: false, error });
      return Promise.reject(error);
    }
  },
  loadUpdateTarget: async (requestBody, withLoading = true) => {
    try {
      if (withLoading) set({ isUpdateLoading: true });
      await putShareTradeTarget(requestBody);
      set({ isUpdateLoading: false, updateError: undefined });

      return null;
    } catch (error) {
      alertMessage({
        alertType: 'plain-default',
        messageType: 'error',
        content: 'Unable to save auto-share',
      });
      set({ isUpdateLoading: false });
      return Promise.reject(error);
    }
  },
  resetShareTradeTarget: () => {
    set(initialState);
  },
}));

export default useShareTradeTarget;
